/**
 * Field Type Selector Modal
 * 
 * Modal for selecting a field type when adding a new field.
 */

import { useState } from 'react';
import {
  X,
  Type,
  Mail,
  Phone,
  Hash,
  Calendar,
  Clock,
  AlignLeft,
  List,
  CircleDot,
  CheckSquare,
  Upload,
  Search,
} from 'lucide-react';
import { useFormBuilder, useSelectedVariant } from '../../contexts';
import type { FieldType } from '@formflow/shared';
import './FieldTypeSelector.css';

// =============================================================================
// Field Type Definitions
// =============================================================================

interface FieldTypeInfo {
  type: FieldType;
  label: string;
  description: string;
  icon: React.ReactNode;
  category: 'basic' | 'choice' | 'advanced';
}

const fieldTypes: FieldTypeInfo[] = [
  {
    type: 'text',
    label: 'Short Text',
    description: 'Single line text input',
    icon: <Type size={20} />,
    category: 'basic',
  },
  {
    type: 'email',
    label: 'Email',
    description: 'Email address with validation',
    icon: <Mail size={20} />,
    category: 'basic',
  },
  {
    type: 'phone',
    label: 'Phone',
    description: 'Phone number input',
    icon: <Phone size={20} />,
    category: 'basic',
  },
  {
    type: 'number',
    label: 'Number',
    description: 'Numeric input with validation',
    icon: <Hash size={20} />,
    category: 'basic',
  },
  {
    type: 'textarea',
    label: 'Long Text',
    description: 'Multi-line text area',
    icon: <AlignLeft size={20} />,
    category: 'basic',
  },
  {
    type: 'date',
    label: 'Date',
    description: 'Date picker',
    icon: <Calendar size={20} />,
    category: 'advanced',
  },
  {
    type: 'time',
    label: 'Time',
    description: 'Time picker',
    icon: <Clock size={20} />,
    category: 'advanced',
  },
  {
    type: 'datetime',
    label: 'Date & Time',
    description: 'Combined date and time picker',
    icon: <Calendar size={20} />,
    category: 'advanced',
  },
  {
    type: 'select',
    label: 'Dropdown',
    description: 'Single selection from a list',
    icon: <List size={20} />,
    category: 'choice',
  },
  {
    type: 'radio',
    label: 'Radio Buttons',
    description: 'Single selection with visible options',
    icon: <CircleDot size={20} />,
    category: 'choice',
  },
  {
    type: 'checkbox',
    label: 'Checkboxes',
    description: 'Multiple selection options',
    icon: <CheckSquare size={20} />,
    category: 'choice',
  },
  {
    type: 'file',
    label: 'File Upload',
    description: 'File attachment (coming soon)',
    icon: <Upload size={20} />,
    category: 'advanced',
  },
];

const categories = [
  { id: 'basic', label: 'Basic Fields' },
  { id: 'choice', label: 'Choice Fields' },
  { id: 'advanced', label: 'Advanced Fields' },
];

// =============================================================================
// Field Type Selector
// =============================================================================

interface FieldTypeSelectorProps {
  onClose: () => void;
}

export function FieldTypeSelector({ onClose }: FieldTypeSelectorProps) {
  const { addField, toggleFieldTypeSelector } = useFormBuilder();
  const variant = useSelectedVariant();
  const [search, setSearch] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const filteredTypes = search
    ? fieldTypes.filter(
        (f) =>
          f.label.toLowerCase().includes(search.toLowerCase()) ||
          f.description.toLowerCase().includes(search.toLowerCase())
      )
    : fieldTypes;

  const handleSelectType = async (type: FieldType) => {
    if (!variant || isCreating) return;
    
    // File upload not yet implemented
    if (type === 'file') {
      alert('File upload is coming soon!');
      return;
    }

    setIsCreating(true);
    try {
      const fieldInfo = fieldTypes.find((f) => f.type === type);
      const existingCount = variant.fields.filter(
        (f) => f.field_type === type
      ).length;
      
      const name = `${type}_${existingCount + 1}`;
      const label = `${fieldInfo?.label || type} ${existingCount + 1}`;

      await addField(variant.id, {
        field_type: type,
        name,
        label,
        required: false,
        is_phi: false,
      });

      toggleFieldTypeSelector(false);
    } catch (error) {
      console.error('Failed to add field:', error);
      setIsCreating(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="field-type-modal">
        <div className="modal-header">
          <h2>Add Field</h2>
          <button className="btn-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <div className="modal-search">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search field types..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        <div className="modal-content">
          {search ? (
            <div className="field-type-grid">
              {filteredTypes.map((field) => (
                <button
                  key={field.type}
                  className="field-type-card"
                  onClick={() => handleSelectType(field.type)}
                  disabled={isCreating}
                >
                  <div className="field-type-icon">{field.icon}</div>
                  <div className="field-type-info">
                    <span className="field-type-label">{field.label}</span>
                    <span className="field-type-description">
                      {field.description}
                    </span>
                  </div>
                </button>
              ))}
              {filteredTypes.length === 0 && (
                <div className="no-results">
                  <p>No field types match your search</p>
                </div>
              )}
            </div>
          ) : (
            categories.map((category) => (
              <div key={category.id} className="field-category">
                <h3 className="category-title">{category.label}</h3>
                <div className="field-type-grid">
                  {fieldTypes
                    .filter((f) => f.category === category.id)
                    .map((field) => (
                      <button
                        key={field.type}
                        className={`field-type-card ${field.type === 'file' ? 'disabled' : ''}`}
                        onClick={() => handleSelectType(field.type)}
                        disabled={isCreating || field.type === 'file'}
                      >
                        <div className="field-type-icon">{field.icon}</div>
                        <div className="field-type-info">
                          <span className="field-type-label">{field.label}</span>
                          <span className="field-type-description">
                            {field.description}
                          </span>
                        </div>
                        {field.type === 'file' && (
                          <span className="coming-soon-badge">Soon</span>
                        )}
                      </button>
                    ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default FieldTypeSelector;
